import React, { useEffect, useMemo, useState, memo, useCallback } from 'react';
import {
  IconDownload,
  IconCopy,
  IconCheck,
  IconClock,
  IconTerminal2,
  IconDeviceDesktop
} from '@tabler/icons-react';

const DownloadIcon = memo(() => <IconDownload className="w-6 h-6" />);
const CopyIcon = memo(() => <IconCopy className="w-4 h-4" />);
const CheckIcon = memo(() => <IconCheck className="w-4 h-4" />);
const OtherIcon = memo(() => <IconClock className="w-6 h-6" />);

const LinuxIcon = memo(() => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 0 0-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 0 0-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139z" />
  </svg>
));

const MacIcon = memo(() => (
  <svg className="w-6 h-6" viewBox="0 0 50 50" fill="currentColor" aria-hidden>
    <path d="M44.527 34.75c-1.078 2.395-1.598 3.465-2.985 5.578-1.942 2.953-4.68 6.641-8.063 6.664-3.012.028-3.789-1.964-7.879-1.929-4.086.02-4.937 1.969-7.953 1.938-3.387-.031-5.977-3.352-7.918-6.301-5.43-8.27-6.004-17.965-2.648-23.121 2.375-3.656 6.129-5.805 9.656-5.805 3.594 0 5.851 1.973 8.82 1.973 2.883 0 4.637-1.977 8.793-1.977 3.141 0 6.461 1.711 8.836 4.664-7.766 4.258-6.504 15.348 1.34 18.317zM31.195 8.469C32.707 6.527 33.855 3.79 33.438 1c-2.465.168-5.348 1.742-7.032 3.781-1.528 1.86-2.793 4.618-2.301 7.286 2.691.086 5.477-1.52 7.09-3.598z" />
  </svg>
));

const WindowsIcon = memo(() => (
  <svg className="w-6 h-6" viewBox="0 0 50 50" fill="currentColor" aria-hidden>
    <path d="M4 4h20v20H4V4zm22 0h20v20H26V4zM4 26h20v20H4V26zm22 0h20v20H26V26z" />
  </svg>
));

const KeeneticIcon = memo(() => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M15.718 10.188 24 2.376h-6.588L9.788 9.812H4.33V2.376H0v19.248h4.33V14.14h7.623c3.2 0 5.412 0.706 6.588 2.07.706 0.8 1.035 1.883 1.035 3.248v2.165h4.33v-2.165c0-2.353-.706-4.424-2.07-6.024-1.46-1.647-3.483-2.776-6.118-3.247" />
  </svg>
));

const baseUrl = 'https://connect.xneon.org/api/cli/download';
const guiBase = 'https://connect.xneon.org/downloads/gui/1.0.0';

const platforms = [
  {
    id: 'linux',
    name: 'Linux',
    description: 'Для Linux систем',
    icon: LinuxIcon,
    architectures: [
      { id: 'amd64', title: 'x86_64 (Intel/AMD)', desc: 'Для стационарных ПК и серверов', archParam: 'amd64', osParam: 'linux' },
      { id: 'arm64', title: 'ARM64 (64-bit)', desc: 'Raspberry Pi 4/5 и подобные', archParam: 'arm64', osParam: 'linux' },
      { id: 'arm', title: 'ARM32 (32-bit)', desc: 'Для старых ARM систем', archParam: 'arm', osParam: 'linux' }
    ]
  },
  {
    id: 'mac',
    name: 'macOS',
    description: 'Для Mac систем',
    icon: MacIcon,
    architectures: [
      { id: 'arm64', title: 'Apple Silicon (M1/M2/M3)', desc: 'Современные Mac', archParam: 'arm64', osParam: 'darwin' },
      { id: 'amd64', title: 'Intel (x86_64)', desc: 'Старые Mac на Intel', archParam: 'amd64', osParam: 'darwin' }
    ]
  },
  {
    id: 'win',
    name: 'Windows',
    description: 'Для Windows систем',
    icon: WindowsIcon,
    architectures: [
      { id: 'amd64', title: 'x86_64 (64-bit)', desc: 'Windows 10/11 64-bit', archParam: 'amd64', osParam: 'windows', isWindows: true },
      { id: 'arm64', title: 'ARM64 (64-bit)', desc: 'Windows на ARM (Surface Pro X)', archParam: 'arm64', osParam: 'windows', isWindows: true }
    ]
  },
  {
    id: 'keenetic',
    name: 'Keenetic Роутеры',
    description: 'Entware / OpenWrt',
    icon: KeeneticIcon,
    architectures: [
      { id: 'mipsel', title: 'MIPSEL', desc: 'Omni, Extra, Giga, Viva, Ultra', archParam: 'mipsel', osParam: 'linux' },
      { id: 'mips', title: 'MIPS', desc: 'Ultra SE, Giga SE, DSL', archParam: 'mips', osParam: 'linux' },
      { id: 'aarch64', title: 'AARCH64', desc: 'Peak, Hopper, KN-1811/1012', archParam: 'aarch64', osParam: 'linux' }
    ]
  },
  {
    id: 'other',
    name: 'Прочие системы',
    description: 'Android CLI, BSD, RISC-V',
    icon: OtherIcon,
    architectures: [
      { id: 'android-arm64', title: 'Android (ARM64)', desc: 'Через Termux / CLI', archParam: 'arm64', osParam: 'android' },
      { id: 'freebsd', title: 'FreeBSD (amd64)', desc: 'Серверы BSD', archParam: 'amd64', osParam: 'freebsd' },
      { id: 'openbsd', title: 'OpenBSD (amd64)', desc: 'OpenBSD 7.x', archParam: 'amd64', osParam: 'openbsd' },
      { id: 'riscv64', title: 'Linux RISC-V (riscv64)', desc: 'Для risc-v устройств', archParam: 'riscv64', osParam: 'linux' }
    ]
  }
];

const guiPlatforms = [
  {
    id: 'win',
    name: 'Windows',
    description: 'Для Windows систем',
    icon: WindowsIcon,
    architectures: [
      { id: 'amd64', title: 'x86_64 (64-bit)', desc: 'Windows 10/11 64-bit', url: `${guiBase}/XN-Connect_1.0.0_x64-setup.exe`, size: '5.18 MB', ext: '.exe' }
    ]
  },
  {
    id: 'mac',
    name: 'macOS',
    description: 'Для Mac систем',
    icon: MacIcon,
    architectures: [
      { id: 'arm64', title: 'Apple Silicon (M1/M2/M3)', desc: 'Современные Mac', url: `${guiBase}/XN-Connect_1.0.0_aarch64.dmg`, size: '6.35 MB', ext: '.dmg' },
      { id: 'amd64', title: 'Intel (x86_64)', desc: 'Старые Mac на Intel', url: `${guiBase}/XN-Connect_1.0.0_x64.dmg`, size: '6.45 MB', ext: '.dmg' }
    ]
  },
  {
    id: 'linux',
    name: 'Linux',
    description: 'Для Linux систем',
    icon: LinuxIcon,
    architectures: [
      { id: 'amd64-appimage', title: 'x86_64 (AppImage)', desc: 'Универсальный', url: `${guiBase}/XN-Connect_1.0.0_amd64.AppImage`, size: '80.6 MB', ext: '.AppImage' },
      { id: 'amd64-deb', title: 'x86_64 (Debian/Ubuntu)', desc: '.deb пакет', url: `${guiBase}/XN-Connect_1.0.0_amd64.deb`, size: '6.73 MB', ext: '.deb' },
      { id: 'amd64-rpm', title: 'x86_64 (Fedora/RHEL)', desc: '.rpm пакет', url: `${guiBase}/XN-Connect-1.0.0_x86_64.rpm`, size: '6.73 MB', ext: '.rpm' }
    ]
  },
  {
    id: 'other',
    name: 'Android',
    description: 'Мобильное приложение',
    icon: OtherIcon,
    architectures: [
      { id: 'universal', title: 'Universal (APK)', desc: 'Для всех устройств', url: `${guiBase}/XN-Connect_1.0.0_universal.apk`, size: '64 MB', ext: '.apk' }
    ]
  }
];

const DownloadPage = () => {
  const [tab, setTab] = useState('cli');
  const [copied, setCopied] = useState(null);
  const [version, setVersion] = useState('');
  const [activePlatformId, setActivePlatformId] = useState(platforms[0].id);
  const activePlatform = useMemo(() => platforms.find(p => p.id === activePlatformId) || platforms[0], [activePlatformId]);
  const [archId, setArchId] = useState(activePlatform.architectures[0].id);

  const [guiPlatformId, setGuiPlatformId] = useState('win');
  const guiActivePlatform = useMemo(() => guiPlatforms.find(p => p.id === guiPlatformId) || guiPlatforms[0], [guiPlatformId]);
  const [guiArchId, setGuiArchId] = useState(guiActivePlatform.architectures[0].id);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const lower = ua.toLowerCase();
    const isWindows = lower.includes('windows');
    const isMac = lower.includes('mac os x') || lower.includes('macintosh');
    const isAndroid = lower.includes('android');
    const isLinux = lower.includes('linux');
    const isArm64 = lower.includes('arm64') || lower.includes('aarch64');
    const isArm = /armv7|armv8|arm;/.test(lower);

    if (isWindows) {
      setActivePlatformId('win');
      setArchId(isArm64 ? 'arm64' : 'amd64');
      setGuiPlatformId('win');
      setGuiArchId('amd64');
    } else if (isMac) {
      setActivePlatformId('mac');
      setArchId(isArm64 ? 'arm64' : 'amd64');
      setGuiPlatformId('mac');
      setGuiArchId(isArm64 ? 'arm64' : 'amd64');
    } else if (isAndroid) {
      setActivePlatformId('other');
      setArchId('android-arm64');
      setGuiPlatformId('other');
      setGuiArchId('universal');
    } else if (isLinux) {
      setActivePlatformId('linux');
      setArchId(isArm64 ? 'arm64' : isArm ? 'arm' : 'amd64');
      setGuiPlatformId('linux');
      setGuiArchId('amd64-appimage');
    }
  }, []);

  useEffect(() => {
    const found = activePlatform.architectures.find(a => a.id === archId);
    if (!found) setArchId(activePlatform.architectures[0].id);
  }, [activePlatform, archId]);

  useEffect(() => {
    const found = guiActivePlatform.architectures.find(a => a.id === guiArchId);
    if (!found) setGuiArchId(guiActivePlatform.architectures[0].id);
  }, [guiActivePlatform, guiArchId]);

  useEffect(() => {
    fetch('/api/cli/version')
      .then(r => r.json())
      .then(data => setVersion(data.version || data.latest || 'latest'))
      .catch(() => setVersion('latest'));
  }, []);

  const selectedArch = useMemo(
    () => activePlatform.architectures.find(a => a.id === archId) || activePlatform.architectures[0],
    [activePlatform, archId]
  );

  const downloadUrl = useMemo(
    () => `${baseUrl}?os=${selectedArch.osParam}&arch=${selectedArch.archParam}`,
    [selectedArch]
  );

  const versionLabel = version || 'latest';

  const command = useMemo(() =>
    selectedArch.isWindows
      ? `# PowerShell:\nInvoke-WebRequest -Uri "${downloadUrl}" -OutFile "xn-connect.exe"\n.\\xn-connect.exe`
      : `curl -fsSL "${downloadUrl}" -o xn-connect && chmod +x xn-connect && ./xn-connect`,
    [selectedArch.isWindows, downloadUrl]
  );

  const copyToClipboard = useCallback((text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 py-20 sm:py-32 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-gray-100 dark:bg-dark-900 border border-gray-200 dark:border-dark-800 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm text-gray-600 dark:text-dark-400">
            <DownloadIcon />
            <span>XN Connect · версия {versionLabel}</span>
          </div>
          <h1 className="text-[28px] sm:text-[32px] lg:text-[40px] font-bold text-gray-900 dark:text-white mt-6 tracking-[-0.5px]">Скачать XN Connect</h1>
          <p className="text-lg text-gray-600 dark:text-dark-500 mt-4">CLI для серверов или GUI приложение для ПК</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-xl bg-gray-100 dark:bg-dark-900 border border-gray-200 dark:border-dark-800 p-1">
            <button
              onClick={() => setTab('cli')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                tab === 'cli'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <IconTerminal2 className="w-4 h-4" />
              CLI
            </button>
            <button
              onClick={() => setTab('gui')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                tab === 'gui'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <IconDeviceDesktop className="w-4 h-4" />
              GUI
            </button>
          </div>
        </div>

        {tab === 'cli' ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 mb-8">
              {platforms.map(p => {
                const Icon = p.icon;
                const isActive = p.id === activePlatformId;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActivePlatformId(p.id)}
                    className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-3 rounded-xl border transition text-center sm:text-left ${
                      isActive
                        ? 'border-primary-500 bg-primary-500/10 text-gray-900 dark:text-white'
                        : 'border-gray-200 dark:border-dark-800 bg-gray-50 dark:bg-dark-900 text-gray-600 dark:text-dark-400 hover:border-primary-500/50 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-gradient-to-br from-primary-500 to-yellow-400 text-black' : 'bg-gray-200 dark:bg-dark-800 text-gray-600 dark:text-dark-400'}`}>
                      <Icon />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm sm:text-base truncate">{p.name}</div>
                      <div className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-500 hidden sm:block">{p.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-500 to-yellow-400 text-black flex items-center justify-center flex-shrink-0">
                  {(() => { const Icon = activePlatform.icon; return <Icon />; })()}
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{activePlatform.name}</h2>
                  <p className="text-gray-600 dark:text-dark-500 text-sm sm:text-base">{activePlatform.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-dark-500 mb-3">Выберите архитектуру:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activePlatform.architectures.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setArchId(a.id)}
                      className={`text-left rounded-xl border transition p-3 sm:p-4 flex flex-col gap-1 ${
                        archId === a.id
                          ? 'border-primary-500 bg-primary-500/10 text-gray-900 dark:text-white'
                          : 'border-gray-200 dark:border-dark-800 bg-gray-50 dark:bg-dark-950 text-gray-600 dark:text-dark-400 hover:border-primary-500/50 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <span className="font-semibold text-sm sm:text-base">{a.title}</span>
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-dark-500">{a.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <a
                href={downloadUrl}
                download
                className="w-full bg-primary-500 hover:bg-primary-600 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition font-medium mb-4"
              >
                <DownloadIcon />
                Скачать v{versionLabel}
              </a>

              <div className="bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-lg p-4 mb-3">
                <p className="text-xs text-gray-600 dark:text-dark-600 mb-2 font-semibold uppercase tracking-wider">КОМАНДА УСТАНОВКИ</p>
                <code className="text-green-600 dark:text-green-400 text-sm block break-words whitespace-pre-wrap font-mono">{command}</code>
              </div>

              <button
                onClick={() => copyToClipboard(command, activePlatform.id)}
                className="w-full bg-gray-100 dark:bg-dark-850 hover:bg-gray-200 dark:hover:bg-dark-800 border border-gray-200 dark:border-dark-800 text-gray-700 dark:text-dark-200 hover:text-gray-900 dark:hover:text-white py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
              >
                {copied === activePlatform.id ? (
                  <><CheckIcon /> Скопировано</>
                ) : (
                  <><CopyIcon /> Скопировать команду</>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-8">
              {guiPlatforms.map(p => {
                const Icon = p.icon;
                const isActive = p.id === guiPlatformId;
                return (
                  <button
                    key={p.id}
                    onClick={() => setGuiPlatformId(p.id)}
                    className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-3 rounded-xl border transition text-center sm:text-left ${
                      isActive
                        ? 'border-primary-500 bg-primary-500/10 text-gray-900 dark:text-white'
                        : 'border-gray-200 dark:border-dark-800 bg-gray-50 dark:bg-dark-900 text-gray-600 dark:text-dark-400 hover:border-primary-500/50 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-gradient-to-br from-primary-500 to-yellow-400 text-black' : 'bg-gray-200 dark:bg-dark-800 text-gray-600 dark:text-dark-400'}`}>
                      <Icon />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm sm:text-base truncate">{p.name}</div>
                      <div className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-500 hidden sm:block">{p.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-500 to-yellow-400 text-black flex items-center justify-center flex-shrink-0">
                  {(() => { const Icon = guiActivePlatform.icon; return <Icon />; })()}
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{guiActivePlatform.name}</h2>
                  <p className="text-gray-600 dark:text-dark-500 text-sm sm:text-base">{guiActivePlatform.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-dark-500 mb-3">Выберите версию:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {guiActivePlatform.architectures.map((a) => {
                    const isSelected = guiArchId === a.id;
                    return (
                      <a
                        key={a.id}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setGuiArchId(a.id)}
                        className={`text-left rounded-xl border transition p-3 sm:p-4 flex flex-col gap-1 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-500/10 text-gray-900 dark:text-white'
                            : 'border-gray-200 dark:border-dark-800 bg-gray-50 dark:bg-dark-950 text-gray-600 dark:text-dark-400 hover:border-primary-500/50 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <span className="font-semibold text-sm sm:text-base">{a.title}</span>
                        <span className="text-xs sm:text-sm text-gray-500 dark:text-dark-500">{a.desc}</span>
                        <span className="text-xs text-primary-500 mt-1">{a.size} · {a.ext}</span>
                      </a>
                    );
                  })}
                </div>
              </div>

              {(() => {
                const selectedGuiArch = guiActivePlatform.architectures.find(a => a.id === guiArchId) || guiActivePlatform.architectures[0];
                return (
                  <a
                    href={selectedGuiArch.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition font-medium mb-4"
                  >
                    <DownloadIcon />
                    Скачать {guiActivePlatform.name} ({selectedGuiArch.ext})
                  </a>
                );
               })()}

            </div>
          </>
        )}

        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8 mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          <div>
            <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-500 to-yellow-400 bg-clip-text text-transparent mb-3">1</div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Скачайте</h3>
            <p className="text-gray-600 dark:text-dark-500 text-sm sm:text-base">Выберите CLI или GUI, скачайте под вашу ОС</p>
          </div>
          <div>
            <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-500 to-yellow-400 bg-clip-text text-transparent mb-3">2</div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Авторизуйтесь</h3>
            <p className="text-gray-600 dark:text-dark-500 text-sm sm:text-base">Войдите с аккаунтом connect.xneon.org</p>
          </div>
          <div>
            <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-500 to-yellow-400 bg-clip-text text-transparent mb-3">3</div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Создавайте туннели</h3>
            <p className="text-gray-600 dark:text-dark-500 text-sm sm:text-base">Настраивайте туннели и пробрасывайте порты</p>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8 mt-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Требования</h2>
          <ul className="text-gray-600 dark:text-dark-400 space-y-3 sm:space-y-4 text-sm sm:text-base">
            <li className="flex items-start gap-2 sm:gap-3"><span className="text-primary-500 font-bold mt-0.5">✓</span><span>Поддерживаемые ОС: Linux, macOS, Windows, Keenetic/OpenWrt, Android (CLI), BSD</span></li>
            <li className="flex items-start gap-2 sm:gap-3"><span className="text-primary-500 font-bold mt-0.5">✓</span><span>Минимум 50 MB свободного места</span></li>
            <li className="flex items-start gap-2 sm:gap-3"><span className="text-primary-500 font-bold mt-0.5">✓</span><span>Стабильное интернет соединение</span></li>
            <li className="flex items-start gap-2 sm:gap-3"><span className="text-primary-500 font-bold mt-0.5">✓</span><span>Аккаунт на connect.xneon.org</span></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
